import { useEffect, useRef } from "react";
import { Heart, Sparkles, Shield, Accessibility, Wifi, ParkingSquare } from "lucide-react";

const features = [
  {
    title: "Women-Owned Small Business",
    icon: Heart,
    desc: "Proudly women-owned, bringing a compassionate touch to community healthcare.",
    color: "text-pink-500",
    bg: "bg-pink-50",
  },
  {
    title: "LGBTQ+ Friendly",
    icon: Sparkles,
    desc: "An inclusive space where everyone is welcomed and respected.",
    color: "text-purple-500",
    bg: "bg-purple-50",
  },
  {
    title: "Transgender Safe Space",
    icon: Shield,
    desc: "A safe, affirming environment for all gender identities.",
    color: "text-blue-500",
    bg: "bg-blue-50",
  },
  {
    title: "Wheelchair Accessible",
    icon: Accessibility,
    desc: "Fully accessible entrance and layout for customers of all abilities.",
    color: "text-teal-600",
    bg: "bg-teal-50",
  },
  {
    title: "Free Wi-Fi Available",
    icon: Wifi,
    desc: "Stay connected while you wait with our complimentary Wi-Fi.",
    color: "text-sky-500",
    bg: "bg-sky-50",
  },
  {
    title: "Free Street Parking",
    icon: ParkingSquare,
    desc: "Easy and free street parking right outside our front door.",
    color: "text-emerald-500",
    bg: "bg-emerald-50",
  },
];

export default function Features() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-us" data-testid="features-section" className="ez-section bg-white" ref={ref}>
      <div className="ez-container">
        <div className="animate-on-view text-center mb-16">
          <div className="inline-block text-ez-teal font-semibold font-caveat text-lg mb-3">
            What Makes Us Different
          </div>
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-ez-text">
            Why <span className="text-ez-teal">Choose Us</span>
          </h2>
          <p className="text-ez-muted mt-4 max-w-xl mx-auto font-jakarta">
            We're more than a pharmacy. We're a community health partner that genuinely cares.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                data-testid={`feature-card-${i}`}
                className="animate-on-view group relative overflow-hidden rounded-2xl border border-gray-100 p-8 hover:border-transparent hover:shadow-[0_12px_24px_rgba(190,10,117,0.2)] transition-all duration-300 hover:-translate-y-1 bg-white"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className={`w-14 h-14 ${feature.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={26} className={feature.color} strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit font-bold text-ez-text text-lg mb-2">{feature.title}</h3>
                <p className="text-ez-muted text-sm font-jakarta leading-relaxed">{feature.desc}</p>

                {/* hover accent */}
                <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full bg-gradient-to-r from-ez-teal to-ez-teal-light transition-all duration-500 rounded-b-2xl" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

